/**
 * SettleParkingSessionModal — T148A-007 (EP-148 Amendment A, ruling i,
 * frontend #2)
 *
 * The operator "Settle & check out" action on an LPR unpaid-candidate row:
 * records an operator-entered amount as a verified cash/transfer payment and
 * checks the underlying parking session out in one server-side transaction
 * (POST /api/parking_session_settlements, built by T148A-006). This modal
 * IS the confirm step itself — no separate ConfirmDialog layered on top.
 *
 * The amount field carries NO default value: no field on LprEvent represents
 * a fee, so a pre-filled number would be fabricated, not derived.
 *
 * A 422 `already_settled` (or any other) response is rendered INLINE inside
 * the modal via `Alert variant="error"` — never a toast, since the session
 * may have just been settled from another tab/operator.
 *
 * EP-161 §10 item 14 (T161-W02a): the server requires a photo of the cash on
 * every cash settlement (cash_photo_key, presigned on the session). Cash
 * shows the same photo step as the gate's "รับเงินสด" (the camera in the
 * app, a file picker on a desktop browser), and Save waits for the photo. A
 * photo the server refuses (stale, invalid) is dropped and asked for again.
 * Transfer is unchanged, and the amount stays typed.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Alert } from '@/components/ui/Alert'
import { useSettleParkingSession } from '@/hooks/useLprEvents'
import { useCashPhoto } from '@/hooks/useParkingSessions'
import { isPhotoRefusal, settleErrorCode } from '@/lib/parkingPhotoUpload'
import { CashPhotoCapture } from '@/components/parking/sessions/CashSettleModal'
import { useNotificationStore } from '@/stores'

export type ParkingSessionSettlementPaymentMethod = 'cash' | 'transfer'

export interface SettleParkingSessionModalProps {
  /** The LPR event's linked parking session — the record the settlement acts on. */
  parkingSessionId: number
  onClose: () => void
}

export function SettleParkingSessionModal({
  parkingSessionId,
  onClose,
}: SettleParkingSessionModalProps) {
  const { t } = useTranslation('parking')
  const addToast = useNotificationStore((s) => s.addToast)
  const settleMutation = useSettleParkingSession()

  // Amount starts empty/undefined — no field on LprEvent represents a fee, so
  // a pre-filled number would be fabricated rather than derived.
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<ParkingSessionSettlementPaymentMethod>('cash')
  const [error, setError] = useState<string | null>(null)
  const photo = useCashPhoto(parkingSessionId)
  const isCash = paymentMethod === 'cash'

  const parsedAmount = Number(amount)
  const isAmountValid = amount.trim() !== '' && !Number.isNaN(parsedAmount) && parsedAmount > 0

  const paymentMethodOptions = [
    { value: 'cash', label: t('lpr.settle.payment_method_cash') },
    { value: 'transfer', label: t('lpr.settle.payment_method_transfer') },
  ]

  const photoReady = !isCash || (!!photo.photo && !photo.uploading)

  async function handleSave() {
    if (!isAmountValid || !photoReady) return
    setError(null)
    try {
      await settleMutation.mutateAsync({
        parking_session_id: parkingSessionId,
        amount: parsedAmount,
        payment_method: paymentMethod,
        ...(isCash && photo.photo ? { cash_photo_key: photo.photo.key } : {}),
      })
      addToast({ type: 'success', message: t('lpr.settle.success') })
      onClose()
    } catch (err) {
      const code = settleErrorCode(err)
      if (isPhotoRefusal(code)) {
        photo.reset(code)
        return
      }
      setError(
        code === 'already_settled'
          ? t('lpr.settle.error_already_settled')
          : t('lpr.settle.error_generic'),
      )
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={t('lpr.settle.modal_title', { id: parkingSessionId })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={settleMutation.isPending}>
            {t('lpr.settle.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            loading={settleMutation.isPending}
            disabled={!isAmountValid || !photoReady}
            data-testid="settle-modal-save"
          >
            {t('lpr.settle.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="error" data-testid="settle-modal-error">
            {error}
          </Alert>
        )}

        <Input
          type="number"
          step="0.01"
          min="0.01"
          label={t('lpr.settle.amount_label')}
          leftIcon={<span className="text-sm font-medium">฿</span>}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="settle-modal-amount"
        />

        <Select
          label={t('lpr.settle.payment_method_label')}
          options={paymentMethodOptions}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value as ParkingSessionSettlementPaymentMethod)}
          data-testid="settle-modal-payment-method"
        />

        {isCash && <CashPhotoCapture state={photo} disabled={settleMutation.isPending} />}
      </div>
    </Modal>
  )
}
